import creditCardType, { types as CardType } from 'credit-card-type';
import { PaymentErrorShortCodes } from '../Interfaces/index.js';
import { PaymentValidationError } from '../Errors/index.js';
var BillingMethodCreditCard = /** @class */ (function () {
    function BillingMethodCreditCard(cardInfo) {
        this.number = BillingMethodCreditCard.formatCard(cardInfo.number);
        var matchedCardTypes = creditCardType(this.number);
        this.type = matchedCardTypes && matchedCardTypes.length > 0 ? matchedCardTypes[0].type : 'unknown';
        this.securityCode = BillingMethodCreditCard.formatSecurityCode(cardInfo.securityCode);
        this.expiration = {
            month: BillingMethodCreditCard.formatExpirationMonth(cardInfo.expiration.month),
            year: BillingMethodCreditCard.formatExpirationYear(cardInfo.expiration.year)
        };
        if (cardInfo.nameOnCard) {
            this.nameOnCard = BillingMethodCreditCard.formatNameOnCard(cardInfo.nameOnCard);
        }
        BillingMethodCreditCard.isValidExpirationDate(this.expiration.month, this.expiration.year);
        BillingMethodCreditCard.isValidSecurityCodeLengthForCardType(this.number, this.securityCode);
    }
    BillingMethodCreditCard.formatCard = function (cardNumber) {
        var filteredCard = cardNumber.replace(/[^0-9]/g, '');
        BillingMethodCreditCard.isValidCardNumber(filteredCard);
        return filteredCard;
    };
    BillingMethodCreditCard.isValidCardNumber = function (cardNumber) {
        var filteredCard = cardNumber.replace(/[^0-9]/g, '');
        if (!/^\d{7,25}$/.test(filteredCard)) {
            throw new PaymentValidationError('Card number is invalid or contains illegal characters. Only numbers, spaces, and dashes are allowed', PaymentErrorShortCodes.invalid_card_number);
        }
        // Validate Luhn check
        return BillingMethodCreditCard.isValidCardNumberLuhn(filteredCard);
    };
    BillingMethodCreditCard.isValidCardNumberLuhn = function (cardNumber) {
        // Format card (for whoever uses this function directly)
        var filteredCard = cardNumber.replace(/[^0-9]/g, '');
        /* code from https://gist.github.com/ShirtlessKirk/2134376 */
        var arr = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
        var len = filteredCard.length;
        var bit = 1;
        var sum = 0;
        var val;
        while (len) {
            val = parseInt(filteredCard.charAt(--len), 10);
            // tslint:disable-next-line:no-bitwise
            // eslint-disable-next-line no-cond-assign
            sum += (bit ^= 1) ? arr[val] : val;
        }
        if (!(sum && sum % 10 === 0)) {
            throw new PaymentValidationError('Card number is invalid (failing Luhn check)', PaymentErrorShortCodes.invalid_card_number_luhn);
        }
        return true;
    };
    BillingMethodCreditCard.formatSecurityCode = function (cardSecurityCode) {
        var filteredSecurityCode = cardSecurityCode.replace(/[^0-9]/g, '');
        BillingMethodCreditCard.isValidCardSecurityCode(filteredSecurityCode);
        return filteredSecurityCode;
    };
    BillingMethodCreditCard.isValidCardSecurityCode = function (cardSecurityCode) {
        if (!/^\d{3,4}$/.test(cardSecurityCode)) {
            throw new PaymentValidationError('Card security code contains illegal characters. Only numbers are allowed', PaymentErrorShortCodes.invalid_card_security_code);
        }
        return true;
    };
    BillingMethodCreditCard.isValidSecurityCodeLengthForCardType = function (card, cardSecurityCode) {
        var matchedCardTypes = creditCardType(card);
        if (!matchedCardTypes || matchedCardTypes.length === 0) {
            return true;
        }
        var cardType = matchedCardTypes[0];
        // American Express cards have a 4-digit security code
        if (cardType.type === CardType.AMERICAN_EXPRESS && cardSecurityCode.length != 4) {
            throw new PaymentValidationError('Card security code is of invalid length', PaymentErrorShortCodes.invalid_card_security_code_length);
        }
        // Diners Club, Discover, Maestro, MasterCard, and Visa cards
        // have a 3-digit security code
        if ([CardType.DINERS_CLUB, CardType.DISCOVER, CardType.MAESTRO, CardType.MASTERCARD, CardType.VISA.toString()].includes(cardType.type) &&
            cardSecurityCode.length != 3) {
            throw new PaymentValidationError('Card security code is of invalid length', PaymentErrorShortCodes.invalid_card_security_code_length);
        }
        return true;
    };
    BillingMethodCreditCard.formatExpirationMonth = function (month) {
        BillingMethodCreditCard.isValidExpirationMonth(month);
        return ('0' + month).slice(-2);
    };
    BillingMethodCreditCard.isValidExpirationMonth = function (month) {
        if (!/^(0?[1-9]|1[012])$/.test(month)) {
            throw new PaymentValidationError('Card expiration month is invalid, must be between 1 and 12', PaymentErrorShortCodes.invalid_expiration_month);
        }
        return true;
    };
    BillingMethodCreditCard.formatExpirationYear = function (year) {
        BillingMethodCreditCard.isValidExpirationYear(year);
        if (year.length === 2) {
            var yearPrefix = new Date().getUTCFullYear().toString().slice(0, 2);
            return "".concat(yearPrefix).concat(year);
        }
        else {
            return year;
        }
    };
    BillingMethodCreditCard.isValidExpirationYear = function (year) {
        if (!/^\d{2}(\d{2})?$/.test(year)) {
            throw new PaymentValidationError('Card expiration year is invalid, must be in either YY or YYYY format', PaymentErrorShortCodes.invalid_expiration_year);
        }
        return true;
    };
    BillingMethodCreditCard.isValidExpirationDate = function (month, year) {
        var currentYear = new Date().getUTCFullYear().toString();
        var currentMonth = (new Date().getMonth() + 1).toString();
        var formattedCurrentMonth = ('0' + currentMonth).slice(-2);
        if (currentYear > year || (currentYear === year && formattedCurrentMonth > ('0' + month).slice(-2))) {
            throw new PaymentValidationError('Card expiration date is in the past', PaymentErrorShortCodes.invalid_expiration_date_in_the_past);
        }
        return true;
    };
    BillingMethodCreditCard.formatNameOnCard = function (nameOnCard) {
        BillingMethodCreditCard.isValidNameOnCard(nameOnCard);
        return nameOnCard;
    };
    BillingMethodCreditCard.isValidNameOnCard = function (nameOnCard) {
        /* regex from https://stackoverflow.com/a/5963425 */
        if (!/^[_.’'\p{L}\p{Mn}\p{Pd}\s]{1,100}$/su.test(nameOnCard)) {
            throw new PaymentValidationError('Name on card contains illegal characters or has illegal format', PaymentErrorShortCodes.invalid_name_on_card);
        }
        return true;
    };
    return BillingMethodCreditCard;
}());
export { BillingMethodCreditCard };
